import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { ArEmailQueueItem, ArEmailQueueMatter, EMAIL_SORT_BY } from '../../types/emailQueue';
import { DataTable, DataTableColumn } from '@xtnd/web/shared/components/DataTable';
import { Icon } from '@repo/ui';
import { numberUtil } from '@xtnd/web/shared/utils';
import { DAYS_PAST_DUE } from '../../constants';
import { getRowUniqueKey, paginateLocal, searchTableLocalByClientName, sortLocal } from './utils';
import { ArMatterChildTable } from './ArMatterChildTable';
import { useMatterARContext } from '../common/MatterARContext';
import { useArEmailCollectionsStore } from '@xtnd/web/shared/stores';
interface ClientsTableProps {
  renderHeader?: () => React.ReactNode;
  onRemoveFromEmailQueue?: (row: ArEmailQueueMatter) => void;
  searchLocal: string;
  setEmailSelected: Dispatch<SetStateAction<ArEmailQueueMatter | null>>;
  setIsPreviewEmail: Dispatch<SetStateAction<boolean>>;
  isLoading: boolean;
}

interface ArClientLocal {
  sorts: Partial<Record<EMAIL_SORT_BY, 'asc' | 'desc'>>;
  page: number;
  limit: number;
}

const LocalClientTableQueryInit: ArClientLocal = {
  page: 1,
  limit: 25,
  sorts: {
    maxDaysPastDue: 'desc',
  },
};

export const ArClientsTable = ({
  renderHeader,
  onRemoveFromEmailQueue,
  setEmailSelected,
  setIsPreviewEmail,
  searchLocal,
  isLoading = false,
}: ClientsTableProps) => {
  const { clients, matters, setMatters } = useArEmailCollectionsStore();
  const [tableParams, setTableParams] = useState(LocalClientTableQueryInit);

  let filtered = searchTableLocalByClientName(clients, searchLocal);
  filtered = sortLocal(filtered, tableParams.sorts);
  const paginated = paginateLocal(filtered, tableParams.page, tableParams.limit);
  const totalPages = Math.ceil(filtered.length / tableParams.limit);

  useEffect(() => {
    if (tableParams.page > totalPages) {
      setTableParams(prev => ({
        ...prev,
        page: totalPages === 0 ? 1 : totalPages,
      }));
    }
  }, [filtered.length, totalPages]);

  const { openMatterDrawer } = useMatterARContext();

  const columns: DataTableColumn<ArEmailQueueItem>[] = useMemo(
    () => [
      {
        key: 'clientName',
        header: 'Client Name',
        align: 'left',
        renderCell: row => (
          <span className="text-sm text-gray-900 dark:text-gray-100">{row?.clientName}</span>
        ),
      },
      {
        key: 'amountOutstanding',
        header: 'Amount Outstanding',
        isSortable: true,
        align: 'left',
        renderCell: row => (
          <span className="text-sm text-gray-900 dark:text-gray-100 max-w-[6rem]">
            {numberUtil.formatCurrency(row?.amountOutstanding)}
          </span>
        ),
      },
      {
        key: 'maxDaysPastDue',
        header: 'DPD',
        align: 'right',
        isSortable: true,
        renderCell: row => (
          <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
            <div className={`text-sm ${row.maxDaysPastDue > DAYS_PAST_DUE ? 'text-red-500' : ''}`}>
              {row?.maxDaysPastDue}
            </div>
          </div>
        ),
      },
      {
        key: 'countMaster',
        header: 'Matters',
        align: 'left',
        renderCell: row => (
          <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
            {row?.matters?.length || 0}
          </span>
        ),
      },
      {
        key: 'action',
        header: '',
        align: 'center',
        renderCell: () => (
          <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
            <button>
              <Icon name="FaAngleRight" size="sm" className="opacity-100" />
            </button>
          </div>
        ),
      },
    ],
    [clients]
  );

  const handleRowClicked = (row: ArEmailQueueItem) => {
    setMatters(row.matters ?? []);
  };

  const isDisplayArMattersTable = useMemo(() => {
    return !!(clients.length > 0 && matters && matters.length > 0);
  }, [matters, clients]);

  const handleClickExternalIcon = (emailIte: ArEmailQueueMatter) => {
    if (emailIte?.matterAdjusterEmail || emailIte.clientEmail) {
      openMatterDrawer(emailIte.matterId, emailIte.clientId);
    }
  };

  return (
    <>
      {isDisplayArMattersTable && clients.length > 0 ? (
        <ArMatterChildTable
          renderHeader={renderHeader}
          onClickExternal={handleClickExternalIcon}
          onRemoveFromEmailQueue={row => onRemoveFromEmailQueue?.(row)}
          onRowClick={row => {
            setEmailSelected(row);
            setIsPreviewEmail(true);
          }}
          searchLocal={searchLocal}
        />
      ) : (
        <DataTable
          columns={columns}
          data={{ data: paginated.data, total: filtered.length }}
          pagination={paginated.pagination}
          onPaginationChange={pagination => {
            setTableParams(prev => ({ ...prev, page: pagination.page, limit: pagination.limit }));
          }}
          stickyHeader
          tableContainerClassName="overflow-y-auto relative"
          tableClassName="min-w-full divide-y divide-gray-200 relative"
          renderHeader={renderHeader}
          onRowClick={handleRowClicked}
          getRowKey={row => getRowUniqueKey(row)}
          onSort={sortBy => setTableParams({ ...tableParams, sorts: { ...sortBy } })}
          sorting={tableParams.sorts}
          isLoading={isLoading}
        />
      )}
    </>
  );
};
